import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { produccionDiariaApi } from '@/services/api'
import type { ProduccionDiariaRow } from '@/types'

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useProduccionDiariaData(desde: string, hasta: string) {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['produccion-diaria', desde, hasta],
    queryFn: () => produccionDiariaApi.getProduccionDiaria({ desde, hasta }),
    staleTime: 5 * 60 * 1000,
    gcTime:    10 * 60 * 1000,
  })

  const rows: ProduccionDiariaRow[] = useMemo(() => {
    const raw = (data?.data ?? []) as ProduccionDiariaRow[]
    return raw.map(r => ({
      ...r,
      qtyProducida:      Number(r.qtyProducida      ?? 0),
      qtyError:          Number(r.qtyError          ?? 0),
      qtyTotalReportada: Number(r.qtyTotalReportada ?? 0),
      qtyPlanificada:    Number(r.qtyPlanificada    ?? 0),
      qtyCalculada:      Number(r.qtyCalculada      ?? 0),
    }))
  }, [data])

  // ── KPIs (computed over full un-filtered dataset) ─────────────────────────
  const kpis = useMemo(() => {
    if (!rows.length) return { totalOps: 0, totalUds: 0, rendimiento: 0, hoyOps: 0, hoyUds: 0 }
    const today = new Date().toISOString().slice(0, 10)
    let totalUds   = 0
    let totalPlan  = 0
    let hoyOps     = 0
    let hoyUds     = 0
    for (const r of rows) {
      totalUds  += r.qtyProducida
      totalPlan += r.qtyPlanificada
      if (r.fecha === today) { hoyOps++; hoyUds += r.qtyProducida }
    }
    const rendimiento = totalPlan > 0 ? Math.round((totalUds / totalPlan) * 100) : 0
    return { totalOps: rows.length, totalUds, rendimiento, hoyOps, hoyUds }
  }, [rows])

  // ── Chart data: qty produced per day ──────────────────────────────────────
  const chartData = useMemo(() => {
    const map = new Map<string, number>()
    for (const r of rows) {
      if (!r.fecha) continue
      map.set(r.fecha, (map.get(r.fecha) ?? 0) + r.qtyProducida)
    }
    return [...map.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([fecha, qty]) => ({ fecha, qty }))
  }, [rows])

  // ── Unique articles for autocomplete ──────────────────────────────────────
  const allArticulos = useMemo(() => {
    const map = new Map<string, string>()
    for (const r of rows) map.set(r.articulo, r.descripcion)
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0], 'es-ES'))
  }, [rows])

  return { rows, isLoading, isError, refetch, kpis, chartData, allArticulos }
}
